'use client';

import { Table } from '@tanstack/react-table';
import { Trash2, Copy } from 'lucide-react';
import * as React from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import {
  DataTableActionBar,
  DataTableActionBarAction,
  DataTableActionBarSelection,
} from '@/components/data-table/data-table-action-bar';
import { Separator } from '@/components/ui/separator';
import { Funnel, useDuplicateFunnel } from '@/lib/api/funnels';
import { useDeleteFunnelsDialog } from './useDeleteFunnelsDialog';

interface FunnelsTableActionBarProps {
  table: Table<Funnel>;
  storeId: string;
}

/**
 * Action bar for bulk operations on selected funnels
 */
export function FunnelsTableActionBar({ table, storeId }: FunnelsTableActionBarProps) {
  const { t } = useTranslation('common')
  const rows = table.getFilteredSelectedRowModel().rows;

  const duplicateMutation = useDuplicateFunnel();
  // Batch-level loading: duplicateMutation.isPending tracks only the latest of
  // the concurrent mutateAsync calls, so we track the whole batch ourselves.
  const [isDuplicating, setIsDuplicating] = React.useState(false);

  // Delete (with demote-first for live funnels) is handled by a shared dialog.
  const deleteDialog = useDeleteFunnelsDialog(storeId, () => table.resetRowSelection());

  const handleBulkDelete = React.useCallback(() => {
    if (!storeId) {
      toast.error(t('funnelsTable.toasts.storeIdRequired'))
      return;
    }
    deleteDialog.open(rows.map((row) => row.original));
  }, [deleteDialog, rows, storeId, t])

  const handleBulkDuplicate = React.useCallback(async () => {
    if (!storeId) {
      toast.error(t('funnelsTable.toasts.storeIdRequired'))
      return;
    }

    const funnelIds = rows.map((row) => row.original.id);

    setIsDuplicating(true);
    try {
      // Duplicate each selected funnel; tally successes/failures so a single
      // bad clone doesn't swallow the rest.
      const results = await Promise.allSettled(
        funnelIds.map((funnelId) =>
          duplicateMutation.mutateAsync({ funnelId, storeId })
        )
      );

      const succeeded = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - succeeded;

      table.resetRowSelection();

      if (succeeded > 0) {
        toast.success(t('funnelsTable.toasts.duplicated', { count: succeeded }))
      }

      if (failed > 0) {
        results.forEach((r) => {
          if (r.status === 'rejected') {
            const message = r.reason instanceof Error ? r.reason.message : t('funnelsTable.toasts.duplicateFailed')
            toast.error(message, { duration: 6000 });
          }
        });
      }
    } finally {
      setIsDuplicating(false);
    }
  }, [duplicateMutation, rows, storeId, t, table])

  return (
    <>
    {deleteDialog.dialog}
    <DataTableActionBar table={table} visible={rows.length > 0}>
      <DataTableActionBarSelection table={table} />
      <Separator orientation="vertical" className="hidden data-[orientation=vertical]:h-5 sm:block" />
      <div className="flex items-center gap-1.5">
        <DataTableActionBarAction
          tooltip={t('funnelsTable.tooltips.duplicateSelected')}
          onClick={handleBulkDuplicate}
          isPending={isDuplicating}
        >
          <Copy className="mr-2 h-4 w-4" />
          {t('funnelsTable.actions.duplicate')}
        </DataTableActionBarAction>

        <DataTableActionBarAction
          tooltip={t('funnelsTable.tooltips.deleteSelected')}
          onClick={handleBulkDelete}
          variant="destructive"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          {t('funnelsTable.actions.delete')}
        </DataTableActionBarAction>
      </div>
    </DataTableActionBar>
    </>
  )
}
